<template>
  <table class="w-full">
    <caption>
      <caption>
        {{
          tableCaption
        }}
        ({{
          $t("Tooltips.ColumnHeadersWithButtonsAreSortable")
        }})
      </caption>
    </caption>
    <thead class="bg-gray-100">
      <tr class="border-b border-gray-300" role="row">
        <th v-if="isSectionSelectable" role="columnheader" scope="col"></th>
        <th
          class="h-14 pl-1 text-left"
          colspan="2"
          role="columnheader"
          scope="col"
          :aria-sort="calculateSortDirectionForColumn('name')"
        >
          <button
            id="name"
            class="flex items-center font-semibold"
            :aria-pressed="!!calculateSortDirectionForColumn('name')"
            @click="sortSections"
          >
            {{ $t("name") }}
            <span class="ml-2" :css-arrow="orderByDirection" aria-hidden="true"></span>
          </button>
        </th>
        <th v-if="$slots.column2Header" class="p-2 text-left" role="columnheader" scope="col">
          <slot name="column2Header"></slot>
        </th>
        <th v-if="$slots.column3Header" class="p-2 text-left" role="columnheader" scope="col">
          <slot name="column3Header"></slot>
        </th>

        <th v-if="$slots.column4Header" class="p-2 text-left" role="columnheader" scope="col">
          <slot name="column4Header"></slot>
        </th>
      </tr>
    </thead>

    <tbody class="border-b border-gray-200">
      <tr
        v-for="section in sortedSections"
        :key="section.sectionId"
        role="row"
        class="border-b border-gray-200 hover:bg-gray-150"
        :class="{ 'cursor-pointer': isSectionSelectable || isSectionClickable }"
        :data-selected="isSectionSelected(section) && isSectionSelectable"
        @click.exact="select(section)"
      >
        <td v-if="isSectionSelectable">
          <div class="flex items-center h-full top-0 pr-2" js-table-checkbox-wrapper>
            <span
              class="flex items-center justify-center w-6 h-6 rounded-full bg-white border border-solid border-gray-200"
              js-table-checkbox
            >
              <span class="w-3 h-3 bg-primary-core rounded-full opacity-0"></span>
            </span>
          </div>
        </td>
        <td class="p-2 w-14">
          <SectionAvatar :section="section" :tooltip="false" :autoload-profile-photo="true" />
        </td>
        <td class="p-2 pl-2 w-auto">
          <a v-if="isSectionClickable" @click.stop.prevent="onClick(section)">{{ section.name }}</a>
          <span v-else> {{ section.name }} </span>
        </td>
        <td v-if="$scopedSlots.column2Content" class="p-2 pr-1 fit-content">
          <slot name="column2Content" :section="section"></slot>
        </td>
        <td v-if="$scopedSlots.column3Content" class="p-2 pr-1 fit-content">
          <slot name="column3Content" :section="section"></slot>
        </td>
        <td v-if="$scopedSlots.column4Content" class="p-2 pr-1 fit-content">
          <slot name="column4Content" :section="section"></slot>
        </td>
      </tr>
    </tbody>
  </table>
</template>

<script>
import SectionAvatar from "@/components/Section/SectionAvatar";
import { usePeopleStore } from "@/stores/people";
import { useSettingsStore } from "@/stores/settings";

export default {
  name: "SectionTable",

  data() {
    return {
      orderByProperty: "name",
      orderByDirection: "asc",
      selectedSectionIds: []
    };
  },

  props: {
    sections: { type: Array, default: () => [] },
    disabledSectionIds: { type: Array, default: () => [], required: false },
    isSectionSelectable: { type: Boolean, default: () => false },
    isSectionClickable: { type: Boolean, default: () => false }
  },

  setup() {
    return {
      peopleStore: usePeopleStore(),
      settingsStore: useSettingsStore()
    };
  },

  mounted() {
    this.loadProfilePicturesForList(this.sections);
  },

  watch: {
    sections(list) {
      this.loadProfilePicturesForList(list);
    }
  },

  computed: {
    sortedSections() {
      const sortedList = this.sections;
      sortedList.sort((a, b) => a.name.localeCompare(b.name, this.settingsStore.language));

      if (this.orderByDirection === "desc") {
        sortedList.reverse();
      }
      return sortedList;
    }
  },

  methods: {
    select(section) {
      if (!this.isSectionSelectable) {
        this.onClick(section);
      }

      if (this.disabledSectionIds && this.disabledSectionIds.indexOf("section.sectionId") > -1) {
        return;
      }

      const index = this.selectedSectionIds.indexOf(section.sectionId);
      if (index > -1) {
        this.selectedSectionIds.splice(index, 1);
      } else {
        this.selectedSectionIds.push(section.sectionId);
      }

      this.$emit("selectedSectionsChanged", this.selectedSections());
    },

    selectedSections() {
      const selectedSectionList = [];
      this.selectedSectionIds.map(si => {
        selectedSectionList.push(this.sections.find(s => s.sectionId === si));
      });
      return selectedSectionList;
    },

    sortSections(evnt) {
      const elmId = evnt.target.id || evnt.target.parentElement.id;
      if (this.orderByProperty === elmId) {
        this.orderByDirection = this.orderByDirection === "asc" ? "desc" : "asc";
        this.$emit("orderByDirectionChanged", this.orderByDirection);
      } else {
        this.orderByProperty = elmId;
        this.$emit("orderByPropertyChanged", this.orderByProperty);
      }
    },

    clearSelection() {
      this.selectedSectionIds = [];
      this.$emit("selectedSectionsChanged", this.selectedSections());
    },

    loadProfilePicturesForList(sectionList) {
      this.peopleStore.loadProfilePicturesFromList(sectionList);
      //Slet
      this.$store.dispatch("sections/sectionsLoadProfilePicturesFromList", sectionList);
    },

    isSectionSelected(section) {
      return this.selectedSectionIds.indexOf(section.sectionId) > -1;
    },

    onClick(section) {
      this.$emit("sectionClick", section);
    },

    calculateSortDirectionForColumn(columnName) {
      if (this.orderByProperty === columnName) {
        return this.orderByDirection === "asc" ? "ascending" : "descending";
      } else {
        return undefined;
      }
    }
  },
  components: {
    SectionAvatar
  }
};
</script>

<style scoped>
.min-width {
  min-width: 1.2rem;
}

td.fit-content {
  width: 1px;
  white-space: nowrap;
}
</style>
